import { POSTS_TYPE_VALUE_ARTICLE } from "@raiden/library/constants/posts";
import { usePreferences } from "@raiden/library/contexts/Preferences";
import { useApi } from "@raiden/library/hooks/useApi";
import { serverApiFetcher } from "@raiden/library/hooks/useApiFetcher";
import generateApiUrl from "@raiden/library/libraries/utils/generateApiUrl";
import generateFrontUri from "@raiden/library/libraries/utils/generateFrontUrl";
import { useIntl } from "react-intl";
import Seo from "../../components/Seo";
import { NewsSearch } from "../../containers/Posts/Articles/Search";

/**
 * @param {object} params
 * @param {number} params.environmentId
 */
function getPostCategoriesUrl({ environmentId }) {
  return generateApiUrl({
    id: "@api.postsCategories.viewAny",
    query: {
      environment_id: [environmentId],
      type: [POSTS_TYPE_VALUE_ARTICLE],
      per_page: 250,
    },
  });
}

/**
 * @typedef {object} Props
 * @property {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/PostCategory").PostCategory[]>} [postCategoriesResponse]
 *
 * @param {Props} props
 */
export default function Page({ postCategoriesResponse }) {
  const intl = useIntl();

  const { bestEnvironment } = usePreferences();

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/PostCategory").PostCategory[]>} */
  const { swrResponse: swrResponsePostCategories } = useApi(
    bestEnvironment
      ? getPostCategoriesUrl({ environmentId: bestEnvironment.id })
      : null,
    {
      swrConfig: {
        fallbackData: postCategoriesResponse,
      },
    },
  );

  return (
    <>
      <Seo
        title={intl.formatMessage({ defaultMessage: "Les actualités" })}
        description={intl.formatMessage({
          defaultMessage: "Suivez notre actualité",
        })}
        links={(locale) =>
          generateFrontUri({ id: "@front.articles", parameters: { locale } })
        }
      />

      <NewsSearch swrResponsePostCategories={swrResponsePostCategories} />
    </>
  );
}

/**
 * @typedef {import("../../types/Page").PageInitialProps & Props} InitialProps
 *
 * @param {import("../../types/AppContext").PageContext} context
 *
 * @returns {Promise<InitialProps>}
 */
Page.getInitialProps = async function (context) {
  /** @type {InitialProps} */
  const values = {};

  if (typeof window === "undefined") {
    const bestEnvironment =
      context.configuration?.environments?.length === 1
        ? context.configuration.environments[0]
        : undefined;

    if (bestEnvironment) {
      /** @type {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/PostCategory").PostCategory[]> | undefined} */
      values.postCategoriesResponse = await serverApiFetcher(
        getPostCategoriesUrl({ environmentId: bestEnvironment.id }),
      ).catch(() => {});
    }
  }

  return values;
};
